/* eslint-disable prefer-rest-params */
import PropTypes from 'prop-types';
import { useCallback, useRef, useMemo } from 'react';
import IntercomContext from 'contexts/intercomContext';
import appConfig from 'config';

function initializeIntercom(id) {
  const w = window;
  const ic = w.Intercom;
  if (typeof ic === 'function') {
    ic('reattach_activator');
    ic('update', w.intercomSettings);
  } else {
    const d = document;
    const i = () => {
      i.c(arguments);
    };

    i.q = [];
    i.c = (args) => {
      i.q.push(args);
    };
    w.Intercom = i;
    const l = () => {
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = `https://widget.intercom.io/widget/${id}`;
      const x = d.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    };
    if (d.readyState === 'complete') {
      l();
    } else if (w.attachEvent) {
      w.attachEvent('onload', l);
    } else {
      w.addEventListener('load', l, false);
    }
  }
}

export default function IntercomProvider({ children, autoBoot = true }) {
  const { appId } = appConfig.intercom;
  const canInitialize = appId;
  const isBooted = useRef(false);
  const isInitialized = useRef(false);
  const missingData = useRef(true);

  const hide = useCallback(() => {
    window.Intercom('hide');
  }, []);

  const show = useCallback(() => {
    window.Intercom('show');
  }, []);

  const boot = useCallback(() => {
    window.intercomSettings = { app_id: appId };
    window.Intercom('boot');
    isBooted.current = true;
  }, [appId, isBooted]);

  const shutdown = useCallback(() => {
    if (!isBooted.current) return;
    window.Intercom('shutdown');
    isBooted.current = false;
    missingData.current = true;
  }, [isBooted, missingData]);

  const update = useCallback(
    (data) => {
      const { language, email, accountId, fullName, intercomHmac, organizationId, organizationName } = data;
      missingData.current = !(language && email && accountId && fullName && intercomHmac && organizationId && organizationName);
      const settings = {
        email,
        missingData,
        language_override: language,
        user_id: accountId,
        name: fullName,
        user_hash: intercomHmac,
        company: {
          company_id: organizationId,
          name: organizationName,
        },
      };

      if (isBooted.current) {
        window.intercomSettings = { ...window.intercomSettings, ...settings };
        window.Intercom('update');
      }
    },
    [isBooted, missingData],
  );

  if (canInitialize && !isInitialized.current) {
    initializeIntercom(appId);

    isInitialized.current = true;

    if (autoBoot) {
      boot();
    }
  }

  const value = useMemo(
    () => ({ boot, shutdown, update, hide, show, isBooted, isInitialized, missingData }),
    [boot, shutdown, update, hide, show, isBooted, isInitialized, missingData],
  );

  return <IntercomContext.Provider value={value}> {children} </IntercomContext.Provider>;
}

IntercomProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]).isRequired,
  autoBoot: PropTypes.bool,
};

IntercomProvider.defaultProps = {
  autoBoot: true,
};
